import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create a dedicated axios instance for API calls
export const api = axios.create({
  baseURL: API,
});

// Set default Content-Type for JSON requests (will be overridden for FormData)
api.defaults.headers.common['Content-Type'] = 'application/json';

// Token getter function - checks both localStorage and sessionStorage
const getAuthToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
};

// Token setter function - saves to localStorage
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
};

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData - browser sets it automatically with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (email, password) => api.post('/login', { email, password }),
  register: (data) => api.post('/register', data),
  getCurrentUser: () => api.get('/me'),
};

// Borrower
export const borrowerAPI = {
  createDeal: (data) => api.post('/borrower/deals', data),
  getDeals: () => api.get('/borrower/deals'),
  getDeal: (id) => api.get(`/borrower/deals/${id}`),
  getDealFees: (id) => api.get(`/borrower/deals/${id}/fees`),
  uploadDocument: (dealId, docType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/borrower/deals/${dealId}/upload?doc_type=${docType}`, formData);
  },
  // Verification endpoints
  getVerification: () => api.get('/borrower/verification'),
  createVerification: () => api.post('/borrower/verification'),
  uploadBankStatement: (statementDate, statementBalance, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/borrower/verification/bank-statement?statement_date=${statementDate}&statement_balance=${statementBalance}`, formData);
  },
};

// LSP
export const lspAPI = {
  createVerification: () => api.post('/lsp/verification'),
  getVerification: () => api.get('/lsp/verification'),
  uploadBankStatement: (statementDate, balance, file, statementNumber = 1) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/lsp/verification/bank-statement?statement_date=${statementDate}&statement_balance=${balance}&statement_number=${statementNumber}`, formData);
  },
  deleteBankStatement: (statementNumber) => api.delete(`/lsp/verification/bank-statement/${statementNumber}`),
  getMarketplace: () => api.get('/lsp/marketplace'),
  createCommitment: (dealId) => api.post(`/lsp/commitments/${dealId}`),
  getCommitments: () => api.get('/lsp/commitments'),
};

// Admin
export const adminAPI = {
  getAllDeals: () => api.get('/admin/deals'),
  getDeal: (id) => api.get(`/admin/deals/${id}`),
  updateDealStatus: (id, status) => api.patch(`/admin/deals/${id}/status?status=${status}`),
  getAllVerifications: () => api.get('/admin/lsp-verifications'),
  updateVerificationStatus: (lspId, status) => api.patch(`/admin/lsp-verifications/${lspId}/status?status=${status}`),
  getAllCommitments: () => api.get('/admin/commitments'),
  updateCommitmentStatus: (id, status) => api.patch(`/admin/commitments/${id}/status?status=${status}`),
  markPaymentPaid: (paymentId, notes) => api.post(`/admin/payments/${paymentId}/mark-paid?notes=${notes || ''}`),
  getAuditLogs: () => api.get('/admin/audit-logs'),
};

// Payments
export const paymentsAPI = {
  // New authorization flow
  setupAuthorization: (dealId) => {
    const originUrl = window.location.origin;
    return api.post(`/payments/setup-authorization?deal_id=${dealId}&origin_url=${originUrl}`);
  },
  confirmAuthorization: (sessionId) => api.post(`/payments/confirm-authorization?session_id=${sessionId}`),
  getAuthorizationStatus: (dealId) => api.get(`/payments/authorization-status/${dealId}`),
  // Legacy (redirects to new flow)
  createCheckout: (dealId) => {
    const originUrl = window.location.origin;
    return api.post(`/payments/checkout?deal_id=${dealId}&origin_url=${originUrl}`);
  },
  getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getDealPayments: (dealId) => api.get(`/payments/deal/${dealId}`),
};

// Plaid
export const plaidAPI = {
  getStatus: () => api.get('/plaid/status'),
  createLinkToken: () => api.post('/plaid/create-link-token'),
  exchangeToken: (publicToken) => api.post(`/plaid/exchange-token?public_token=${publicToken}`),
  verifyBalance: () => api.post('/plaid/verify-balance'),
};

// Documents
export const documentsAPI = {
  upload: (dealId, docType, audience, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/upload?deal_id=${dealId}&doc_type=${docType}&audience=${audience}`, formData);
  },
  getAll: () => api.get('/documents/'),
  getDealDocuments: (dealId) => api.get(`/documents/deal/${dealId}`),
  download: (documentId) => api.get(`/documents/download/${documentId}`, { responseType: 'blob' }),
  sign: (documentId, signatureData) => api.post(`/documents/sign/${documentId}`, { signature_data: signatureData }),
};

// Notifications
export const notificationsAPI = {
  getAll: () => api.get('/notifications/'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Signatures
export const signaturesAPI = {
  signDocument: (documentId, signatureData, signatureType, signerName) => 
    api.post('/signatures/sign', {
      document_id: documentId,
      signature_data: signatureData,
      signature_type: signatureType,
      signer_name: signerName
    }),
  getDocumentSignatures: (documentId) => api.get(`/signatures/document/${documentId}`),
  getDealSignatures: (dealId) => api.get(`/signatures/deal/${dealId}`),
  verifySignature: (signatureId) => api.get(`/signatures/verify/${signatureId}`),
  getMySignatures: () => api.get('/signatures/my-signatures'),
};

// Document Workflow (Templates & Signing)
export const docWorkflowAPI = {
  getStageDocuments: (stage) => api.get(`/doc-workflow/required/${stage}`),
  getMyDocuments: () => api.get('/doc-workflow/my-documents'),
  getDealDocuments: (dealId) => api.get(`/doc-workflow/deal/${dealId}`),
  signDocument: (documentKey, signatureData, signatureType, signerName, dealId = null, formData = null) =>
    api.post('/doc-workflow/sign', {
      document_key: documentKey,
      signature_data: signatureData,
      signature_type: signatureType,
      signer_name: signerName,
      deal_id: dealId,
      form_data: formData
    }),
  // Admin endpoints
  getPendingDocuments: () => api.get('/doc-workflow/admin/pending-review'),
  getDocumentDetails: (signatureId) => api.get(`/doc-workflow/admin/document/${signatureId}`),
  updateDocumentStatus: (signatureId, status, notes = null) =>
    api.patch(`/doc-workflow/admin/document/${signatureId}/status?status=${status}${notes ? '&notes=' + encodeURIComponent(notes) : ''}`),
  getDealAllDocuments: (dealId) => api.get(`/doc-workflow/admin/deal/${dealId}/documents`),
};